import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Phone, User, Users, Edit2, PlayCircle, CheckCircle, Briefcase } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Assignment, Technician } from '@shared/types';
import { ASSIGNMENT_STATUS_CONFIG } from '@shared/constants/status';

interface AssignmentListProps {
    assignments: Array<{ assignment: Assignment; technicians: Technician[] }>;
    onEditAssignment: (assignment: Assignment, techIds: string[]) => void;
    onStartWorksite: (assignmentId: string) => void;
    onFinishWorksite: (assignmentId: string) => void;
}

export const AssignmentList: React.FC<AssignmentListProps> = ({
    assignments,
    onEditAssignment,
    onStartWorksite,
    onFinishWorksite
}) => {
    if (assignments.length === 0) {
        return (
            <div className="glass-panel p-8 text-center">
                <Briefcase size={48} className="mx-auto text-muted mb-3" />
                <p className="text-muted">Aucun chantier trouvé</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {assignments.map(({ assignment, technicians: assignedTechs }) => (
                <motion.div
                    key={assignment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-panel p-4 hover:bg-white/10 transition-all group"
                >
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            {/* Header */}
                            <div className="flex items-start gap-3 mb-3">
                                <Calendar size={20} className="text-accent flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 mb-1">
                                            {assignment.worksiteId && (
                                                <span className="text-[10px] font-mono tracking-widest uppercase text-accent/80 bg-accent/10 px-2 py-0.5 rounded border border-accent/20">
                                                    {assignment.worksiteId}
                                                </span>
                                            )}
                                            <h3 className="font-semibold text-main text-lg">{assignment.clientName}</h3>
                                        </div>
                                        {/* Status Badge */}
                                        {(() => {
                                            const status = assignment.status || 'pending';
                                            const style = ASSIGNMENT_STATUS_CONFIG[status] || ASSIGNMENT_STATUS_CONFIG['pending'];
                                            const Icon = style.icon;
                                            return (
                                                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${style.color}`}>
                                                    <Icon size={12} />
                                                    {style.label}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    <div className="text-sm text-muted">
                                        📅 {format(parseISO(assignment.startDate), 'dd MMM yyyy', { locale: fr })} → {format(parseISO(assignment.endDate), 'dd MMM yyyy', { locale: fr })}
                                        {assignment.includeWeekends && (
                                            <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">WE</span>
                                        )}
                                    </div>
                                    {(assignment.startTime || assignment.endTime) && (
                                        <div className="text-sm text-muted mt-1">
                                            🕐 {assignment.startTime || '--:--'} - {assignment.endTime || '--:--'}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm ml-8">
                                {assignment.type && (
                                    <div className="flex items-center gap-1.5">
                                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-semibold rounded border border-blue-500/30">
                                            {assignment.type}
                                        </span>
                                    </div>
                                )}

                                {assignment.task && (
                                    <div className="text-main">
                                        <span className="font-medium">Tâche :</span> {assignment.task}
                                    </div>
                                )}

                                {(assignment.address || assignment.city) && (
                                    <div className="flex items-start gap-1.5 text-muted">
                                        <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                                        <span>{assignment.address}{assignment.address && ', '}{assignment.postalCode} {assignment.city}</span>
                                    </div>
                                )}

                                {assignment.contactName && (
                                    <div className="flex items-center gap-1.5 text-muted">
                                        <User size={14} />
                                        <span>{assignment.contactName}</span>
                                    </div>
                                )}

                                {assignment.contactPhone && (
                                    <div className="flex items-center gap-1.5 text-muted">
                                        <Phone size={14} />
                                        <span>{assignment.contactPhone}</span>
                                    </div>
                                )}
                            </div>

                            {/* Assigned Technicians */}
                            <div className="flex items-center gap-2 ml-8 mt-3 flex-wrap">
                                <Users size={16} className="text-gray-500" />
                                <span className="text-xs text-gray-500">Affecté à :</span>
                                {assignedTechs.length === 0 ? (
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-500 border border-red-500/20">
                                        ⚠️ Non assigné
                                    </div>
                                ) : (
                                    assignedTechs.map(tech => (
                                        <div
                                            key={tech.id}
                                            className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                                            style={{ backgroundColor: `${tech.color}20`, border: `1px solid ${tech.color}40` }}
                                        >
                                            <div
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: tech.color }}
                                            />
                                            <span style={{ color: tech.color }}>{tech.name}</span>
                                            <span className="text-gray-500">({tech.service})</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all">
                            {/* Edit Button */}
                            <button
                                onClick={() => onEditAssignment(assignment, assignedTechs.map(t => t.id))}
                                className="p-2 rounded-lg bg-[var(--button-bg)] border border-[var(--button-border)] text-muted hover:text-accent hover:border-accent/50 hover:bg-accent/10 transition-all"
                                title="Modifier le chantier"
                            >
                                <Edit2 size={18} />
                            </button>

                            {/* Start Button */}
                            {(!assignment.status || assignment.status === 'pending') && (
                                <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => onStartWorksite(assignment.id)}
                                    className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300 transition-all"
                                    title="Démarrer le chantier"
                                >
                                    <PlayCircle size={18} />
                                </motion.button>
                            )}

                            {/* Finish Button */}
                            {assignment.status === 'in-progress' && (
                                <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => onFinishWorksite(assignment.id)}
                                    className="p-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 hover:text-green-300 transition-all"
                                    title="Terminer le chantier"
                                >
                                    <CheckCircle size={18} />
                                </motion.button>
                            )}
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
};
